/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import { Autosuggestions } from '@msdyn365-commerce/commerce-entities';
import MsDyn365, { getUrlSync } from '@msdyn365-commerce/core';
import { ProductRefiner } from '@msdyn365-commerce/retail-proxy';
import { createSearchStateInput } from '@msdyn365-commerce-modules/search-utilities';
import getUnbxdSearchAutoSuggestData, { GetUnbxdSearchAutoSuggestDataInput } from './actions/get-unbxd-search-autosuggest.action';
import { getTelemetryObject, IModuleProps, INodeProps, ITelemetryContent, KeyCodes, getPayloadObject, onTelemetryClick } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { debounce } from 'lodash';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react';
import * as React from 'react';
import Cookies from 'universal-cookie';
import { FormComponent } from './components/mfrm-autosuggest.form';
import { IMfrmAutosuggestData } from './mfrm-autosuggest.data';
import { IMfrmAutosuggestProps } from './mfrm-autosuggest.props.autogenerated';
import getSearchProducts, { GetFullProductsBySearchInput } from '../mfrm-search/actions/get-full-products-by-search';
import getSociData, { GetSociDataInput } from '../../actions/get-soci-data.action';

export interface ISearchState {
    isSearchFormExpanded: boolean;
    searchText: string;
    selectedQuery: string;
    suggestions: Autosuggestions | undefined | any;
    searchRefiners?: ProductRefiner[] | undefined;
    autoSuggestAriaLabel: string;
    isInitialState: boolean;
    isLoadingAutoSuggest?: boolean;
    isFocused?: boolean;
    sociLocationsList?: any[];
}

export interface IMfrmAutosuggestViewProps extends IMfrmAutosuggestProps<IMfrmAutosuggestData> {
    Search: IModuleProps;
    AutoSuggestAriaLabel: INodeProps;
    AutoSuggestAriaLabelText: string;
    searchText: string;
    selectedQuery: string;
    AutoSuggest: INodeProps;
    SearchForm: INodeProps;
    FormWrapper: INodeProps;
    form: React.ReactNode;
    callbacks: {
        handleCancelSearchChange(): void;
    };
    isSearchFormExpanded: boolean;
    isLoadingAutoSuggest?: boolean;
    isLoadingNode?: React.ReactNode;
    suggestions?: any;
    suggestedSociLocations?: any[];
    isFocused?: boolean;
    handleCloseDialog(e: React.MouseEvent<HTMLElement>): void;
    recentlySearched?: string[];
    buildLink(searchTerm: string): string;
    handleUnbxdRedirect(query: string): Promise<string>;
    setFormText(e: React.MouseEvent<HTMLDivElement>, searchText: string): void;
}

/**
 *
 * Search module.
 * @extends {React.Component<IMfrmAutosuggestProps<IMfrmAutosuggestData>, ISearchState>}
 */
class MfrmAutosuggest extends React.Component<IMfrmAutosuggestProps<IMfrmAutosuggestData>, ISearchState> {
    private readonly searchTextInput: React.RefObject<HTMLInputElement>;

    private readonly searchCollapsedLabel: React.RefObject<HTMLButtonElement>;

    private readonly formReference: React.RefObject<HTMLButtonElement>;

    private readonly maxChars: number;

    private readonly waitTime: number = 500;

    private readonly autoSuggestResultScreenReaderDiv: React.RefObject<HTMLDivElement>;

    private readonly autoSuggestResultDiv: React.RefObject<HTMLDivElement>;

    private readonly searchFormClass: string;

    // private readonly telemetryContent: ITelemetryContent;

    // @ts-expect-error
    private cancellabelDebounce: debounce;
    public cookies: Cookies = new Cookies();
    public recentlySearched: [] = [];


    constructor(props: IMfrmAutosuggestProps<IMfrmAutosuggestData>) {
        super(props);
        this.searchTextInput = React.createRef();
        this.searchCollapsedLabel = React.createRef();
        this.autoSuggestResultScreenReaderDiv = React.createRef();
        this.autoSuggestResultDiv = React.createRef();
        this.formReference = React.createRef();
        this.state = {
            isSearchFormExpanded: false,
            searchText: '',
            suggestions: undefined,
            autoSuggestAriaLabel: '',
            isInitialState: true,
            isLoadingAutoSuggest: false,
            isFocused: false,
            selectedQuery: '',
            sociLocationsList: []
        };
        this.maxChars =
            (this.props.context &&
                this.props.context.app &&
                this.props.context.app.config &&
                this.props.context.app.config.searchInputMaxLength) ||
            50;
        this.searchFormClass = 'ms-search__form';
        this.setFormText = this.setFormText.bind(this);
    }

    public async componentDidMount(): Promise<void> {
        document.body && document.body.addEventListener('mousedown', this._focusOutTarget);
        window && window.addEventListener('keyup', this._keyup);
        this.recentlySearched = this.cookies.get('ReacentlySearches');
    }

    public componentWillUnmount(): void {
        document.body && document.body.removeEventListener('mousedown', this._focusOutTarget, false);
        window && window.removeEventListener('keyup', this._keyup, false);
    }

    public async componentDidUpdate(): Promise<void> {
        const {
            config: { hideSearchLabel }
        } = this.props;
        const { isSearchFormExpanded, isInitialState, searchText, sociLocationsList } = this.state;
        if (!this.props.context.request.params.isEditor) {
            if (!hideSearchLabel && isSearchFormExpanded) {
                this.searchTextInput && this.searchTextInput.current && this.searchTextInput.current.focus();
            } else if (!isInitialState) {
                this.searchCollapsedLabel && this.searchCollapsedLabel.current && this.searchCollapsedLabel.current.focus();
            }
        }
        if (!searchText && !this.state.suggestions) {
            void this._getSearchSuggestionsData('*', 3, 0, 3, 0, 0); // initial query with overrides for proper suggestions
            sociLocationsList?.length! > 0 && this.setState({ sociLocationsList: [] });
        }
    }

    public shouldComponentUpdate(nextProps: IMfrmAutosuggestProps<IMfrmAutosuggestData>, nextState: ISearchState): boolean {
        if (this.state === nextState && this.props === nextProps) {
            return false;
        }
        return true;
    }

    public setFormText(e: any, selectedQuery: string): void {
        this.setState({ selectedQuery },
            () => this._handleSubmit(e)
        );
    }

    // eslint-disable-next-line complexity -- ignore complexity.
    public render(): JSX.Element {
        const {
            context,
            resources: { /*searchtext, searchLabelArialLabel, */cancelBtnAriaLabel, searchBtnAriaLabel, submitBtnAriaLabel },
            config: { hideSearchLabel, disableSubmitSearch, searchplaceholderText, isParentCustomModule, iconURL, className = '' }
        } = this.props;
        const {
            isSearchFormExpanded,
            searchText,
            suggestions,
            isLoadingAutoSuggest,
            isFocused,
            sociLocationsList
        } = this.state;
        const searchQsp = (context && context.app && context.app.config && context.app.config.searchQueryStringParameter) || 'q';
        const searchURL = getUrlSync('search', context && context.actionContext) || '';
        const searchPlaceholder = searchplaceholderText && searchplaceholderText.length > 0 ? searchplaceholderText : 'Search in Fabrikam';
        const classSearchModal = `${sociLocationsList?.length && suggestions?.response?.numberOfProducts > 0 ?
            'ms-search__autoSuggest search-result-stores-product-suggestion' : sociLocationsList?.length && !this.recentlySearched ?
                'ms-search__autoSuggest  search-result-only-stores' : sociLocationsList?.length && this.recentlySearched ?
                    'ms-search__autoSuggest' :
                    'ms-search__autoSuggest'}`;
        const viewProps: IMfrmAutosuggestViewProps = {
            ...(this.props as IMfrmAutosuggestProps<IMfrmAutosuggestData>),

            Search: {
                moduleProps: this.props,
                className: `${classnames('ms-search', className)} ${disableSubmitSearch ? 'no-submit' : 'with-submit'}`
            },
            AutoSuggestAriaLabel: {
                tag: 'div',
                className: 'msc-autoSuggest__screen-reader',
                'aria-live': 'assertive',
                ref: this.autoSuggestResultScreenReaderDiv
            },
            AutoSuggestAriaLabelText: this.state.autoSuggestAriaLabel,
            searchText: this.state.searchText,
            selectedQuery: this.state.selectedQuery,
            AutoSuggest: {
                ref: this.autoSuggestResultDiv,
                className: classSearchModal
            },
            SearchForm: {
                className: `${this.searchFormClass} ${isSearchFormExpanded ? 'bx-show' : 'bx-hide'}`
            },
            FormWrapper: {
                tag: 'form',
                ref: this.formReference,
                className: 'ms-search__searchForm',
                'aria-label': 'Search',
                name: 'searchForm',
                role: 'search',
                action: searchURL,
                autoComplete: 'off',
                onSubmit: this._handleSubmit
            },
            form: FormComponent({
                ariaLabelCancel: cancelBtnAriaLabel,
                ariaLabelSearch: searchBtnAriaLabel,
                ariaLabelSubmit: submitBtnAriaLabel,
                hideSearchLabel,
                searchTextInput: this.searchTextInput,
                disableSubmitSearch,
                handleInputChange: this._handleInputChange,
                handleCancelSearchChange: this._handleCancelSearchChange,
                maxChars: this.maxChars,
                searchQsp,
                searchPlaceholder,
                query: searchText,
                handleFocused: this.handleFocused,
                onSubmit: this._handleSubmit,
                isParentCustomModule: isParentCustomModule,
                iconURL: iconURL
            }),
            callbacks: {
                handleCancelSearchChange: this._handleCancelSearchChange
            },
            isSearchFormExpanded: this.state.isSearchFormExpanded,
            isLoadingAutoSuggest,
            isLoadingNode: this._isLoadingNode(),
            suggestions,
            suggestedSociLocations: sociLocationsList ?? [],
            isFocused,
            handleCloseDialog: this.handleCloseDialog,
            recentlySearched: this.recentlySearched,
            buildLink: this.buildLink,
            handleUnbxdRedirect: this._handleUnbxdRedirect,
            setFormText: this.setFormText
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private buildLink = (searchTerm: string) => {
        const { context } = this.props;
        const params = MsDyn365.isBrowser && window.location;
        const origin = params && params.origin === 'https://localhost:4000' ? params.origin : context.request.url.requestUrl.origin;
        const searchQsp = (context && context.app && context.app.config && context.app.config.searchQueryStringParameter) || 'q';
        const searchPageURL = getUrlSync('search', context && context.actionContext) || '';
        return `${origin}${searchPageURL}?${searchQsp}=${searchTerm.split(' ').join('%20')}`;
    };

    /**
     * Function to create the ReactNode for loading Autosuggest result.
     * @returns ReachNode for loading Autosuggest result.
     */
    private readonly _isLoadingNode = (): React.ReactNode => {
        const loadingText = this.props.resources.autoSuggestResultLoadingMessage;
        return <Spinner className='msc-autoSuggest__loadingResult' label={loadingText} size={SpinnerSize.medium} labelPosition='right' />;
    };

    private readonly _focusOutTarget = (e: any): void => {
        if (e.target.closest('.ms-search') === null) {
            this._clearAutoSuggestState();
        }
    };

    private readonly _keyup = (e: KeyboardEvent): void => {
        const { suggestions } = this.state;

        if (suggestions && suggestions.AllSearchResults && e.keyCode === KeyCodes.Escape) {
            this._clearAutoSuggestState();
        } else if (this.state.isSearchFormExpanded && e.keyCode === KeyCodes.Escape) {
            if (this.state.isSearchFormExpanded) {
                this.setState({ isSearchFormExpanded: false });
            }
        }

        if (
            !(
                (this.formReference.current && this.formReference.current.contains((e.target as unknown) as Node)) ||
                (this.autoSuggestResultDiv.current && this.autoSuggestResultDiv.current.contains((e.target as unknown) as Node))
            )
        ) {
            if (suggestions && suggestions.AllSearchResults) {
                this._clearAutoSuggestState();
            }

            if (this.state.isSearchFormExpanded) {
                this.setState({ isSearchFormExpanded: false });
            }

            if (this.state.searchText.length === 0) {
                this._clearAutoSuggestState();
            }
        }
    };

    private readonly _clearAutoSuggestState = (): void => {
        const {
            context: { actionContext },
            data: { searchState }
        } = this.props;

        if (this.searchTextInput.current && this.searchTextInput.current.value.length > 0) {
            searchState.searchText = '';
            searchState.defaultBasicSearchUrl = '';
            searchState.searchQueryStringParameter = '';
            actionContext ? actionContext.update(createSearchStateInput(searchState), searchState) : null;
            this.searchTextInput.current.value = '';
        }
        document.body.classList.remove('no-scroll');

        this.setState({ isFocused: false, searchText: '', suggestions: undefined, searchRefiners: undefined, sociLocationsList: [] });
    };

    private handleFocused = (): void => {
        const telemetryContent: ITelemetryContent = getTelemetryObject('Autosuggest Search Bar', this.props.friendlyName, this.props.telemetry);
        const payLoad = getPayloadObject('click', telemetryContent, 'Autosuggest Input Click', '');
        onTelemetryClick(telemetryContent, payLoad, 'Autosuggest Input Click')();
        const { isFocused } = this.state;
        if (!isFocused) {
            this.setState({ isFocused: true });
        }
        document.body.classList.add('no-scroll');
    };

    private handleCloseDialog = (e: any) => {
        e.preventDefault();
        this._clearAutoSuggestState();
    };

    private readonly _handleInputChange = (query: string): void => {

        const {
            context
        } = this.props;

        if (!query || query.length <= this.maxChars) {
            if (!query && this.state.suggestions) {
                this.setState({ suggestions: undefined });
            }
            this.setState({ searchText: query }, () => {
                if (context) {
                    if (query && query.length > 0) {
                        if (query.length > this.maxChars) {
                            return;
                        }
                        const { searchText } = this.state;
                        this.cancellabelDebounce = debounce(() => {
                            this.setState({ isLoadingAutoSuggest: true });
                            void this._getSearchSuggestionsData(
                                searchText,
                                this.props.config.popularProductCount,
                                this.props.config.inFieldCount,
                                this.props.config.topQueriesCount,
                                this.props.config.keyWordSuggestionCount,
                                this.props.config.promotedSuggestionCount
                            );
                            void this._getSociStore();
                        }, this.waitTime);

                        setTimeout(() => {
                            this.cancellabelDebounce();
                        }, 0);
                    }
                }
            });
        }

        if (this.cancellabelDebounce) {
            this.cancellabelDebounce.cancel();
        }
    };

    private readonly _handleSubmit = async (
        e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement> | React.FormEvent<HTMLFormElement>, eventType?: string
    ): Promise<void> => {
        const {
            config: { disableSubmitSearch },
            context: {
                actionContext,
                app: {
                    config: { searchQueryStringParameter }
                }
            }
        } = this.props;

        const query = (this.state.selectedQuery.length > 0 && this.state.selectedQuery) || (this.searchTextInput.current && this.searchTextInput.current.value);

        if (!query || (query && query.length === 0) || disableSubmitSearch) {
            this.searchTextInput && this.searchTextInput.current && this.searchTextInput.current.focus();
            e.preventDefault();
        } else {
            // @ts-ignore
            if ('utag' in window) {
                // @ts-ignore
                utag.link({
                    event_type: "site_search",
                    search_term: query,
                    search_type: eventType === 'search-box-icon' ? 'search-box-icon' : 'search-box-enter'
                });
            }
            this._setRecentSearches(query);
            const searchQsp = searchQueryStringParameter || 'q';
            const searchURL = this._appendQueryParams(getUrlSync('search', actionContext) || '', query, searchQsp);
            const redirectUrl = await this._handleUnbxdRedirect(query);

            if (query && MsDyn365.isBrowser) {
                document.location.href = (redirectUrl ? redirectUrl : searchURL)?.replace(`/${this.props.app.config.wmapPageUrlSuffix}`, '');
            }

        }
    };

    private _handleUnbxdRedirect = async (query: string): Promise<string> => {
        const input = new GetFullProductsBySearchInput(
            'Search',
            this.props.context.request.apiSettings,
            {
                Paging: {
                    Top: 1,
                    Skip: 0
                },
                count: true,
                Sorting: {}
            },
            [],
            + (this.props.context.request.urlTokens.itemId || 0),
            query,
            false,
            this.props.context.request.locale,
            this.props.context.request.url.requestUrl.pathname
        );
        let redirectUrl = '';
        if (input?.searchText) {
            const productResults = await getSearchProducts(input, this.props.context.actionContext);
            const extProperties = productResults.products && productResults.products.length > 0 && productResults.products[0]?.ExtensionProperties;
            const redirectUrlValue = extProperties && extProperties?.find(obj => obj?.Key === 'RedirectURL')?.Value?.StringValue;
            redirectUrlValue && redirectUrlValue.length ? redirectUrl = redirectUrlValue : redirectUrl = '';
            return redirectUrl;
        }
        return redirectUrl;
    };

    private _setRecentSearches = (query: string): void => {
        const cookies = new Cookies();
        let inserted = false;

        let recentlySearches = cookies.get(`ReacentlySearches`);
        if (query !== undefined) {
            if (recentlySearches === undefined || recentlySearches.length === 0) {
                const recentlySearches = [];
                recentlySearches.push(query);
                cookies.set(`ReacentlySearches`, recentlySearches, { path: '/' });
            } else {
                recentlySearches?.map((item: string, index: number) => {
                    if (item === query) {
                        recentlySearches?.splice(index, 1);
                        recentlySearches = [query]?.concat(recentlySearches);
                        cookies.set(`ReacentlySearches`, recentlySearches, { path: '/' });
                        inserted = true;
                    }
                });

                if (!inserted) {
                    if (recentlySearches.length >= 3) {
                        recentlySearches?.splice(-1);
                        recentlySearches = [query]?.concat(recentlySearches);
                        cookies.set(`ReacentlySearches`, recentlySearches, { path: '/' });
                    } else {
                        recentlySearches = [query]?.concat(recentlySearches);
                        cookies.set(`ReacentlySearches`, recentlySearches, { path: '/' });
                    }
                    inserted = false;
                }
            }
        }
    };

    private readonly _handleCancelSearchChange = (): void => {
        const { isSearchFormExpanded, suggestions } = this.state;

        if (!isSearchFormExpanded) {
            this.setState({
                isSearchFormExpanded: true,
                searchText: '',
                isInitialState: false
            });
        } else {
            if (suggestions && suggestions.AllSearchResults) {
                this._clearAutoSuggestState();
            }

            this.setState({ isSearchFormExpanded: false });
        }
    };

    private readonly _appendQueryParams = (route: string, query: string, qsp: string): string => {
        if (!MsDyn365.isBrowser) {
            return '';
        }

        const queryUrl = new URL(route, window.location.href);
        if (qsp && query) {
            queryUrl.searchParams.set(qsp, query);
        }
        return queryUrl.href;
    };

    private _getSearchSuggestionsData = async (
        searchText: string,
        popularProductCount: number | undefined,
        inFieldCount: number | undefined,
        topQueryCount: number | undefined,
        keywordCount: number | undefined,
        promoCount: number | undefined
    ): Promise<void> => {
        try {
            getUnbxdSearchAutoSuggestData(
                new GetUnbxdSearchAutoSuggestDataInput(
                    searchText,
                    popularProductCount,
                    inFieldCount,
                    topQueryCount,
                    keywordCount,
                    promoCount
                ),
                this.props.context.actionContext
            ).then((data: any) => {
                if (data) {
                    this.setState({ suggestions: data });
                }
            }).catch((e: any) => console.error(e));
        } catch (error) {
            console.error('Unbxd API error', Error);
        }
    };

    private _sortSociLocations = (stores: any) => {
        const preferredStore = localStorage?.getItem('_mfrm__prefferedStore_') || null;
        if (stores?.length !== 0 && preferredStore) {
            const parsedPreferredStore = JSON.parse(preferredStore);
            const selectedStoreIndex = stores?.findIndex((store: any) => parsedPreferredStore?.name === store.name);
            const selectedStore = stores?.splice(selectedStoreIndex, 1);
            stores.unshift(selectedStore[0]);
            return [...new Set(stores)];
        }
        return stores;
    };

    private _getSociStore = async (): Promise<void> => {
        try {
            const { maxNoOfSociLocations, lookupRadius } = this.props.config;
            const { searchText } = this.state;
            getSociData(new GetSociDataInput(lookupRadius, maxNoOfSociLocations, searchText), this.props.context.actionContext).then((data: any) => {
                if (data) {
                    const sociAvailableStores = data.response.collection && data.response.collection.filter((store: { opening_status: string; }) => store?.opening_status !== 'permanently_closed') || [];
                    const sortedLocations = this._sortSociLocations(sociAvailableStores);
                    this.setState({ sociLocationsList: sortedLocations });
                }
            }).catch((e) => console.error(e));

        } catch (error) {
            console.error('Soci API error', Error);
        }
    };
}

export default MfrmAutosuggest;